import { getWebOutbox } from "@client/iphone/lib/videoSync/createVideoSync";
import { type StorageLike, type SyncFeature } from "@client/sync";
import { createSyncFeature } from "@client/sync";
import { trpcVanilla } from "@client/trpc";
import { type WatchSegment } from "./watchSegments";

export interface WatchSegmentPayload {
  segmentId: string;
  videoId: string;
  startedAt: string;
  endedAt: string;
  playheadStartSeconds: number;
  playheadEndSeconds: number;
  playbackRate: number;
  screenVisible: boolean;
}

export function toWatchSegmentPayload(
  segment: WatchSegment,
): WatchSegmentPayload {
  return {
    segmentId: segment.segmentId,
    videoId: segment.videoId,
    startedAt: new Date(segment.startedAtMs).toISOString(),
    endedAt: new Date(segment.endedAtMs).toISOString(),
    playheadStartSeconds: segment.playheadStartSeconds,
    playheadEndSeconds: segment.playheadEndSeconds,
    playbackRate: segment.playbackRate,
    screenVisible: segment.screenVisible,
  };
}

/**
 * Durable outbox for wall-clock watch segments (SP-2690), a sibling of
 * `videoStatesProgressSync` with the same storage split: native persists to
 * localStorage (segments accrued offline survive a force-quit and flush on
 * reconnect), web uses the per-user in-memory outbox. The storage key is
 * per-user AND distinct from every other sync feature's key — two workers
 * over one queue double-send.
 *
 * Coalescing is by SEGMENT id, not video id: while a segment grows, each
 * refresh replaces its queued entry in place (a queue never holds two
 * versions of one segment), while a NEW segment for the same video appends.
 * The builder only ever refreshes the currently-open segment, and the server
 * upserts by id with an endedAt-grows guard, so replay/resend order can't
 * corrupt anything.
 */
export function createWatchSegmentSync(args: {
  userId: string;
  isNative: boolean;
  // Test seams; production uses the real defaults.
  storage?: StorageLike;
  isOnline?: () => boolean;
  // See createSyncFeature — commit gate for sends racing an identity flip.
  shouldCommit?: () => boolean;
}): SyncFeature<WatchSegmentPayload> {
  return createSyncFeature<WatchSegmentPayload, { applied: boolean }>({
    storageKey: `videoStatesSync.watchSegments.v1.${args.userId}`,
    storage:
      args.storage ?? (args.isNative ? undefined : getWebOutbox(args.userId)),
    coalesce: (existing, incoming) =>
      existing.segmentId === incoming.segmentId ? incoming : null,
    send: (payload) => trpcVanilla.recordVideoWatchSegment.mutate(payload),
    isOnline: args.isOnline,
    shouldCommit: args.shouldCommit,
  });
}
